/**
 * Query hooks for site onboarding, device integration and attribute definitions.
 *
 * Cache keys are deliberately granular. The device queue and its per-status counts are
 * separate keys because the counts badge the sidebar on every page, while the queue only
 * matters on one — invalidating them together would refetch a 200-row list on every
 * navigation.
 */
import { useEffect } from 'react';
import type { IndustryId } from '@/lib/compliance';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { installRoleCatalogue } from '@/lib/topology/model';
import { pollInterval } from './backoff';
import { api } from './client';
import type { FieldGroup } from '@/components/form/schema';
/* The canonical archetype union, from the compliance engine — restating it here would give
   two lists to keep in step with one database enum. */
import type { ArchetypeId } from '@/lib/compliance';

/* ------------------------------------------------------------------- types */

export type DeviceStatus = 'pending' | 'allowed' | 'blocked' | 'retired';

export interface Device {
  id: number;
  siteId: string;
  siteName: string;
  deviceName: string;
  label: string | null;
  vendor: string;
  model: string;
  deviceType: string;
  csuType: string | null;
  status: DeviceStatus;
  statusReason: string | null;
  roleKey: string | null;
  roleLabel: string | null;
  /**
   * Which plant this device is filed into, or NULL for site-wide equipment.
   *
   * NULL is not a gap to be tidied away. A meter measures the whole site and belongs to no
   * plant, and site-wide equipment is visible to anyone assigned the site — it is where the
   * site's own alarms land, so a narrowing that hid it would render a clean board about a
   * plant that is fine and a site that is not (`Docs/08-views/ALARMS.md` §7).
   */
  plantId: string | null;
  parentDeviceId: number | null;
  /**
   * Which UNIT behind a gateway this is, when it is one — `dse335`, `pcc3300`, `room_sensor`.
   *
   * NULL for every device that named itself on the wire. It is the ONLY thing separating a
   * machine the ingest gate derived from a gateway's key prefixes from one an operator
   * parented by hand: both carry `parentDeviceId`, and only this says which.
   */
  unitKey: string | null;
  /** The gateway this unit was DISCOVERED THROUGH. Never an electrical link — see
   *  `parentDeviceId`, which stays null on a derived unit since migration 0009. */
  gatewayDeviceId: number | null;
  firstSeenAt: string;
  lastSeenAt: string;
  allowedAt: string | null;
  payloadCount: number;
  sampleKeys: string[] | null;
  sampleKeyCount: number;
  /** Series SHOWN under this device — a combined reading counts on exactly one device. */
  seriesCount: number;
  /** Set when this is a virtual device an operator combined (`device_combines`). */
  combineId: string | null;
  /** How many devices a virtual device draws its readings from. 0 for a wire device. */
  combinedFrom: number;
  /** How many of this device's registers are shown under a virtual device instead. */
  sharedOut: number;
}

export interface DeviceRole {
  key: string;
  label: string;
  icon: string;
  industry: string | null;
  chainOrder: number;
  /** Null means this role is not a point in the power chain. It is still measured. */
  nodeKind: string | null;
  /** Equipment family: power, storage, generation, cooling, load, control, metering, … */
  category: string;
}

export interface DevicePreviewParam {
  key: string;
  displayName: string | null;
  unit: string | null;
  kind: string;
  confidence: string;
  /**
   * `resolved` — configuration already in effect for this metric.
   * `catalog`  — a default the platform will apply on first sight; nothing stored yet.
   * `unknown`  — no rule matched; it will be captured with no unit.
   */
  source: 'resolved' | 'catalog' | 'unknown';
  /** On a virtual device's list, the device this reading really comes from. Null otherwise. */
  sourceDeviceName: string | null;
}

/** A virtual device some of this device's registers are shown under instead. */
export interface DeviceSharedWith {
  deviceId: number;
  name: string;
  registers: number;
}

/**
 * DERIVED, not restated.
 *
 * This was a hand-written union of the same five ids the compliance package already
 * declares, and it drifted the moment ten industries were added: the server accepted
 * `healthcare` and this type rejected it, so the onboarding form could not offer what the
 * API supported. A second literal is a second place to forget.
 *
 * `IndustryId` comes from the vendored compliance engine, which is synced byte-identically
 * from the backend and guarded by a digest — so this cannot fall behind the server's own
 * enum without the digest check failing first.
 */
export type Industry = IndustryId;

/* -------------------------------------------------------------- definitions */

/**
 * The form schema. Cached hard: definitions change when a platform admin adds a field,
 * which is rare, and refetching an 80-field schema on every step of a wizard makes the
 * wizard feel broken.
 */
/**
 * The field schema for a form, narrowed by industry AND ARCHETYPE.
 *
 * `archetype` is the axis that has existed on `attribute_definitions` since migration 0002 and
 * that nothing ever passed, so a hyperscale data hall and a 5G edge cabinet in the same industry
 * received a byte-identical form. Measured through the service once wired: a telecom central
 * office drops 33 fields / 9 groups to 31 / 8 — losing the "Tower & sharing" section a building
 * cannot answer — and a datacenter edge node drops 130 fields to 105.
 *
 * IT IS IN THE QUERY KEY, and that is not a formality. `staleTime` here is ten minutes, so a key
 * that omitted the archetype would serve the FIRST archetype's schema to every other archetype
 * for ten minutes — a form silently showing the wrong fields, which is far worse than showing
 * too many.
 *
 * A FOURTH, OPTIONAL AXIS since migration 0081: `role` — a `site_devices.role_key`, narrowing a
 * `scope: 'device'` schema to what THAT device type is offered (`DeviceNameplatePanel`'s edit
 * form is the one caller that passes it, from the resolved read's own `roleKey`). Omitted, this
 * keeps the pre-0081 behaviour — every device-scope field the industry/archetype axes admit —
 * which is deliberate for any future caller that has not been taught a device's role yet.
 */
export function useAttributeDefinitions(
  scope: 'site' | 'device',
  industry?: Industry,
  archetype?: ArchetypeId,
  role?: string,
  /** Read as this organisation rather than the header's — Onboarding's chosen organisation,
      whose own fields a site created there will carry. Absent, the header's. */
  organisationId?: string,
): UseQueryResult<{ groups: FieldGroup[]; suggestions?: string[] }> {
  return useQuery({
    queryKey: [
      'attribute-definitions',
      scope,
      industry ?? null,
      archetype ?? null,
      role ?? null,
      organisationId ?? null,
    ],
    queryFn: () => {
      const query = {
        scope,
        ...(industry ? { industry } : {}),
        ...(archetype ? { archetype } : {}),
        ...(role ? { role } : {}),
      };
      return organisationId
        ? api.getIn<{ groups: FieldGroup[]; suggestions?: string[] }>('/attribute-definitions', query, organisationId)
        : api.get<{ groups: FieldGroup[]; suggestions?: string[] }>('/attribute-definitions', query);
    },
    staleTime: 10 * 60_000,
  });
}

export function useSiteAttributes(siteId: string | null) {
  return useQuery({
    queryKey: ['site-attributes', siteId],
    queryFn: () => api.get<{ values: Record<string, unknown> }>(`/sites/${siteId}/attributes`),
    enabled: Boolean(siteId),
  });
}

export function useSaveSiteAttributes(siteId: string, organisationId?: string) {
  const qc = useQueryClient();
  return useMutation({
    /* Saved as the site's own organisation when Onboarding named one, so the fields are checked
       against the definitions the form was drawn from — that organisation's, not the header's. */
    mutationFn: (values: Record<string, unknown>) =>
      organisationId
        ? api.putIn<{ values: Record<string, unknown> }>(`/sites/${siteId}/attributes`, { values }, organisationId)
        : api.put<{ values: Record<string, unknown> }>(`/sites/${siteId}/attributes`, { values }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['site-attributes', siteId] });
      // The commissioning checklist is derived from these values, so it is now stale.
      void qc.invalidateQueries({ queryKey: ['commissioning', siteId] });
    },
  });
}

export function useCommissioningGaps(siteId: string | null) {
  return useQuery({
    queryKey: ['commissioning', siteId],
    queryFn: () =>
      api.get<{ missing: Array<{ key: string; label: string; groupLabel: string }> }>(
        `/sites/${siteId}/commissioning`,
      ),
    enabled: Boolean(siteId),
  });
}

/* ------------------------------------------------------------------ devices */

export function useDevices(params: {
  status?: DeviceStatus;
  siteId?: string;
  q?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['devices', params],
    queryFn: () =>
      api.get<{ data: Device[]; nextCursor: string | null }>('/devices', {
        ...(params.status ? { status: params.status } : {}),
        ...(params.siteId ? { siteId: params.siteId } : {}),
        ...(params.q ? { q: params.q } : {}),
        limit: params.limit ?? 50,
      }),
    /*
     * Devices are discovered by the ingest service on its own schedule (sites publish
     * every 180s), so a queue left open must refresh itself or an operator watches a
     * stale "0 pending" while data is being discarded.
     */
    refetchInterval: pollInterval(60_000),
  });
}

/**
 * EVERY device at a site, following the keyset cursor to the end.
 *
 * `useDevices` takes a `limit` and drops `nextCursor`, and the backend caps that limit at
 * 200 — so any caller that needed the whole inventory was silently getting the first page.
 * SLD Studio's seed dialog was one: a 260-device data centre seeded 200 devices, and the
 * missing boards and loads simply did not exist in the graph. The outage sweep then found
 * fewer single points of failure than the plant has, and the verdict panel reported a
 * redundancy level for a plant 60 devices short, with nothing on screen to say so.
 *
 * `pagesFetched` and `truncated` are returned rather than hidden: a caller that hits the
 * page cap has to be able to SAY it did. A silently short inventory is the failure mode
 * this replaces, and an unbounded loop against a paginated endpoint is the other one.
 */
export function useAllDevices(siteId: string | null, maxPages = 25) {
  return useQuery({
    queryKey: ['devices', 'all', siteId, maxPages],
    enabled: siteId !== null,
    queryFn: async () => {
      const out: Device[] = [];
      let cursor: string | null = null;
      let pages = 0;
      do {
        const page: { data: Device[]; nextCursor: string | null } = await api.get<{
          data: Device[];
          nextCursor: string | null;
        }>('/devices', {
          ...(siteId ? { siteId } : {}),
          limit: 200,
          ...(cursor ? { cursor } : {}),
        });
        out.push(...page.data);
        cursor = page.nextCursor;
        pages += 1;
      } while (cursor && pages < maxPages);
      return { data: out, pagesFetched: pages, truncated: cursor !== null };
    },
    refetchInterval: pollInterval(60_000),
  });
}

export function useDeviceCounts() {
  return useQuery({
    queryKey: ['device-counts'],
    queryFn: () => api.get<{ counts: Record<DeviceStatus, number> }>('/devices/counts'),
    refetchInterval: pollInterval(60_000),
  });
}

export function useDeviceRoles(industry?: string) {
  const q = useQuery({
    queryKey: ['device-roles', industry ?? null],
    queryFn: () =>
      api.get<{ roles: DeviceRole[] }>('/devices/roles', industry ? { industry } : undefined),
    staleTime: 10 * 60_000,
  });

  /*
   * INSTALLED INTO THE TOPOLOGY MODEL as a side effect of the fetch, so the diagram's role
   * handling comes from the catalogue rather than a table compiled into the bundle.
   * `model.ts` is pure and synchronous — it cannot fetch — and its role functions are
   * called from code that has no business carrying a catalogue, so this is where the two
   * meet.
   *
   * Idempotent, and the model keeps its built-in fallback until this lands, so a slow or
   * failed fetch degrades to the previous behaviour rather than to an empty diagram.
   */
  const roles = q.data?.roles;
  useEffect(() => {
    if (roles !== undefined) installRoleCatalogue(roles);
  }, [roles]);

  return q;
}

export function useDevicePreview(deviceId: number | null) {
  return useQuery({
    queryKey: ['device-preview', deviceId],
    queryFn: () =>
      api.get<{ device: Device; parameters: DevicePreviewParam[]; sharedWith: DeviceSharedWith[] }>(
        `/devices/${deviceId}/preview`,
      ),
    enabled: Boolean(deviceId),
  });
}

/** Everything a status change invalidates. Listed once so no call site forgets one. */
function invalidateDeviceViews(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['devices'] });
  void qc.invalidateQueries({ queryKey: ['device-counts'] });
  // Integrating a device starts data flowing, which changes the fleet health numbers.
  void qc.invalidateQueries({ queryKey: ['telemetry-health'] });
}

export function useIntegrateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: number;
      label?: string;
      roleKey?: string;
      parentDeviceId?: number | null;
    }) => {
      const { id, ...body } = input;
      return api.post<{ device: Device }>(`/devices/${id}/integrate`, body);
    },
    onSuccess: () => invalidateDeviceViews(qc),
  });
}

export function useBulkIntegrate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { deviceIds: number[]; roleKey?: string }) =>
      api.post<{ integrated: number; skipped: number }>('/devices/integrate', input),
    onSuccess: () => invalidateDeviceViews(qc),
  });
}

export function useSetDeviceStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number; action: 'block' | 'retire'; reason: string }) =>
      api.post<{ device: Device }>(`/devices/${input.id}/${input.action}`, {
        reason: input.reason,
      }),
    onSuccess: () => invalidateDeviceViews(qc),
  });
}

export function usePatchDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: number;
      label?: string;
      roleKey?: string;
      parentDeviceId?: number | null;
    }) => {
      const { id, ...body } = input;
      return api.patch<{ device: Device }>(`/devices/${id}`, body);
    },
    onSuccess: () => invalidateDeviceViews(qc),
  });
}

export function useSaveDeviceAttributes(deviceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      api.put<{ values: Record<string, unknown> }>(`/devices/${deviceId}/attributes`, { values }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['device-attributes', deviceId] }),
  });
}

export function useDeviceAttributes(deviceId: number | null) {
  return useQuery({
    queryKey: ['device-attributes', deviceId],
    queryFn: () =>
      api.get<{ values: Record<string, unknown> }>(`/devices/${deviceId}/attributes`),
    enabled: Boolean(deviceId),
  });
}

/* -------------------------------------------------------------------- sites */

export interface CreateSiteInput {
  name: string;
  slug: string;
  industry: Industry;
  archetype: string;
  region: string;
  city?: string;
  lat?: number;
  lng?: number;
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    /*
     * `under` names the organisation the site belongs to, for this one request (`api.postIn`),
     * instead of the header's — Onboarding's Organisation box (Hamzah, 2026-10-14: where you are
     * looking is where it is created, without changing the header). Never in the body: the server
     * takes the owner from `X-Lumen-Organisation` and checks it against the caller's reach.
     */
    mutationFn: ({ under, ...input }: CreateSiteInput & { under?: string }) =>
      under
        ? api.postIn<{ site: { id: string; name: string; slug: string } }>('/sites', input, under)
        : api.post<{ site: { id: string; name: string; slug: string } }>('/sites', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sites'] });
      void qc.invalidateQueries({ queryKey: ['telemetry-health'] });
    },
  });
}
